import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Send, Timer, Trash2 } from "lucide-react";
import { papi, errText, localISO } from "../portalApi";
import { Card, EmptyState, Field, PageHeader, Spinner, StatusBadge, fmtDate, inputCls } from "../ui";

const mondayOf = (dateStr) => {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return localISO(d);
};

const shiftWeek = (weekStart, days) => {
  const d = new Date(`${weekStart}T00:00:00`);
  d.setDate(d.getDate() + days);
  return localISO(d);
};

const WD_ORDER = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const wdOf = (dateStr) => WD_ORDER[(new Date(`${dateStr}T00:00:00`).getDay() + 6) % 7];

const defaultDate = (data) => {
  const workDays = data.workDays || WD_ORDER.slice(0, 6);
  const todayStr = localISO(new Date());
  if (todayStr >= data.weekStart && todayStr <= data.weekEnd && workDays.includes(wdOf(todayStr))) return todayStr;
  for (let i = 0; i < 7; i += 1) {
    const dt = shiftWeek(data.weekStart, i);
    if (workDays.includes(wdOf(dt))) return dt;
  }
  return data.weekStart;
};

export default function Timesheets() {
  const [weekStart, setWeekStart] = useState(() => mondayOf(localISO(new Date())));
  const [data, setData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({ date: "", projectId: "", task: "", description: "", hours: "", billable: false });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [t, p] = await Promise.all([
        papi.get("/timesheets", { params: { weekStart } }),
        papi.get("/projects"),
      ]);
      setData(t.data);
      setProjects(p.data);
      setForm((f) => ({ ...f, date: f.date && f.date >= t.data.weekStart && f.date <= t.data.weekEnd && (t.data.workDays || WD_ORDER.slice(0, 6)).includes(wdOf(f.date)) ? f.date : defaultDate(t.data) }));
    } catch (err) {
      toast.error(errText(err, "Could not load the timesheet."));
    }
  }, [weekStart]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <Spinner />;

  const workDays = data.workDays || WD_ORDER.slice(0, 6);
  const dayHours = {};
  data.entries.forEach((en) => { dayHours[en.date] = Math.round(((dayHours[en.date] || 0) + en.hours) * 10) / 10; });
  const filledDays = WD_ORDER.filter((wd, i) => workDays.includes(wd) && dayHours[shiftWeek(data.weekStart, i)]).length;

  const addEntry = async (e) => {
    e.preventDefault();
    if (!(data.workDays || WD_ORDER.slice(0, 6)).includes(wdOf(form.date))) {
      toast.error("That day is your weekly off — pick a working day.");
      return;
    }
    setSaving(true);
    try {
      const proj = projects.find((p) => p.id === form.projectId);
      await papi.post("/timesheets/entries", {
        ...form,
        hours: parseFloat(form.hours),
        projectName: proj?.name || "",
        brand: proj?.brand || "",
      });
      toast.success("Entry added.");
      setForm({ ...form, task: "", description: "", hours: "", billable: false });
      load();
    } catch (err) {
      toast.error(errText(err, "Could not add the entry."));
    } finally {
      setSaving(false);
    }
  };

  const removeEntry = async (id) => {
    try {
      await papi.delete(`/timesheets/entries/${id}`);
      toast.success("Entry removed.");
      load();
    } catch (err) {
      toast.error(errText(err, "Could not remove the entry."));
    }
  };

  const submit = async () => {
    if (!window.confirm("Submit all pending entries for approval? Submitted entries are locked, but you can keep adding entries for the other days.")) return;
    try {
      await papi.post("/timesheets/submit", { weekStart: data.weekStart });
      toast.success("Timesheet submitted for review.");
      load();
    } catch (err) {
      toast.error(errText(err, "Could not submit the timesheet."));
    }
  };

  const thisWeek = mondayOf(localISO(new Date()));

  return (
    <div data-testid="timesheets-page">
      <PageHeader title="Timesheets" sub="Log your work hours against projects, then submit the week for approval.">
        <div className="flex items-center gap-2">
          <button onClick={() => setWeekStart(shiftWeek(weekStart, -7))} className="rounded-lg border border-navy-900/15 bg-white p-2 text-navy-900 transition-colors hover:border-gold" aria-label="Previous week" data-testid="week-prev">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button onClick={() => setWeekStart(thisWeek)} className="rounded-lg border border-navy-900/15 bg-white px-3 py-2 text-xs font-semibold text-navy-900 transition-colors hover:border-gold" data-testid="week-current">
            This Week
          </button>
          <button onClick={() => setWeekStart(shiftWeek(weekStart, 7))} className="rounded-lg border border-navy-900/15 bg-white p-2 text-navy-900 transition-colors hover:border-gold" aria-label="Next week" data-testid="week-next">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </PageHeader>

      <Card className="mb-6" testid="week-summary-card">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-serif text-xl text-navy-900" data-testid="week-range">
              {fmtDate(data.weekStart)} — {fmtDate(data.weekEnd)}
            </p>
            <p className="mt-1 text-sm text-slate-500">
              <span className="font-semibold text-navy-900" data-testid="week-total-hours">{data.totalHours}h</span> logged · {data.entries.length} entries
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="workday-chips">
              {WD_ORDER.map((wd, i) => workDays.includes(wd) && (
                <button
                  key={wd}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, date: shiftWeek(data.weekStart, i) }))}
                  className={`rounded-lg border px-2.5 py-1 text-[11px] font-semibold transition-colors ${dayHours[shiftWeek(data.weekStart, i)] ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-amber-300 bg-amber-50 text-amber-700"}`}
                  data-testid={`workday-chip-${wd}`}
                >
                  {wd} {dayHours[shiftWeek(data.weekStart, i)] ? `${dayHours[shiftWeek(data.weekStart, i)]}h` : "·"}
                </button>
              ))}
              <span className="text-[11px] text-slate-500" data-testid="workdays-progress">{filledDays} of {workDays.length} work days logged</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge status={data.status} />
            {data.pendingCount > 0 && (
              <button onClick={submit} className="btn-gold !px-5 !py-2.5 !text-xs" data-testid="submit-timesheet-btn">
                <Send className="h-4 w-4" aria-hidden /> Submit {data.pendingCount} Pending
              </button>
            )}
          </div>
        </div>
      </Card>

      <Card className="mb-6" testid="add-entry-card">
          <p className="mb-4 flex items-center gap-2 text-sm font-semibold text-navy-900">
            <Plus className="h-4 w-4 text-gold-dark" aria-hidden /> Add Entry
          </p>
          <form onSubmit={addEntry} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-6" data-testid="timesheet-entry-form">
            <Field label="Date">
              <input type="date" required min={data.weekStart} max={data.weekEnd} className={inputCls} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="entry-date" />
            </Field>
            <Field label="Project">
              <select required className={inputCls} value={form.projectId} onChange={(e) => setForm({ ...form, projectId: e.target.value })} data-testid="entry-project">
                <option value="">Select project</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <div className="lg:col-span-2">
              <Field label="Task">
                <input required className={inputCls} placeholder="What did you work on?" value={form.task} onChange={(e) => setForm({ ...form, task: e.target.value })} data-testid="entry-task" />
              </Field>
            </div>
            <Field label="Hours">
              <input type="number" required min="0.5" max="16" step="0.5" className={inputCls} placeholder="8" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} data-testid="entry-hours" />
            </Field>
            <div className="flex items-end">
              <button type="submit" disabled={saving} className="btn-gold w-full justify-center !px-4 !py-2.5 !text-xs disabled:opacity-60" data-testid="entry-add-btn">
                {saving ? "Adding…" : "Add"}
              </button>
            </div>
            <div className="sm:col-span-2 lg:col-span-5">
              <input className={inputCls} placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="entry-description" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600 lg:col-span-1">
              <input type="checkbox" checked={form.billable} onChange={(e) => setForm({ ...form, billable: e.target.checked })} className="h-4 w-4 accent-gold" data-testid="entry-billable" />
              Billable
            </label>
          </form>
        </Card>

      {data.entries.length === 0 ? (
        <EmptyState icon={Timer} title="No entries this week" sub="Add your first time entry above to start tracking this week." testid="timesheet-empty" />
      ) : (
        <Card className="!p-0 overflow-hidden" testid="timesheet-table-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="timesheet-table">
              <thead>
                <tr className="border-b border-navy-900/10 bg-ivory text-left">
                  {["Date", "Project", "Task", "Hours", "Billable", "Status", ""].map((h) => (
                    <th key={h} className="px-5 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.entries.map((e) => (
                  <tr key={e.id} className="border-b border-navy-900/5 last:border-0 hover:bg-ivory/60" data-testid={`timesheet-entry-${e.id}`}>
                    <td className="px-5 py-3 font-semibold text-navy-900">{fmtDate(e.date)}</td>
                    <td className="px-5 py-3 text-slate-600">{e.projectName || "General / Internal"}</td>
                    <td className="px-5 py-3 text-slate-600">
                      {e.task}
                      {e.description && <p className="mt-0.5 text-xs text-slate-400">{e.description}</p>}
                    </td>
                    <td className="px-5 py-3 font-semibold text-navy-900">{e.hours}h</td>
                    <td className="px-5 py-3 text-slate-600">{e.billable ? "Yes" : "—"}</td>
                    <td className="px-5 py-3"><StatusBadge status={e.status || "Draft"} /></td>
                    <td className="px-5 py-3 text-right">
                      {(e.status || "Draft") === "Draft" && (
                        <button onClick={() => removeEntry(e.id)} className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600" aria-label="Delete entry" data-testid={`delete-entry-${e.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
